import React from 'react';
import { Calendar, Clock, CalendarDays, Briefcase, CalendarClock, User, Milestone, Star } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatDate } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета промежутка времени
 */
const ResultsDisplay = ({ results, calculationType }) => {
  if (!results) return null;
  
  // Форматирование даты для отображения
  const formatDisplayDate = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      weekday: 'long'
    });
  };
  
  // Склонение слова "день" в зависимости от числа
  const formatDaysLabel = (days) => {
    const lastDigit = days % 10;
    const lastTwoDigits = days % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'дней';
    if (lastDigit === 1) return 'день';
    if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
    return 'дней';
  };

  // Склонение слова "неделя" в зависимости от числа
  const formatWeeksLabel = (weeks) => {
    const lastDigit = weeks % 10;
    const lastTwoDigits = weeks % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'недель';
    if (lastDigit === 1) return 'неделя';
    if (lastDigit >= 2 && lastDigit <= 4) return 'недели';
    return 'недель';
  };

  // Склонение слова "месяц" в зависимости от числа
  const formatMonthsLabel = (months) => {
    const lastDigit = months % 10;
    const lastTwoDigits = months % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'месяцев';
    if (lastDigit === 1) return 'месяц';
    if (lastDigit >= 2 && lastDigit <= 4) return 'месяца';
    return 'месяцев';
  };

  // Стандартный результат расчета разницы между датами
  if (calculationType === 'standard') {
    return (
      <ToolResult title="Результаты расчета промежутка времени">
        {/* Ключевые метрики */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          {/* Дни */}
          <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
            <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
              <Calendar className="w-4 h-4"/>
              Дней
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-red-800 dark:text-red-200" aria-live="polite">
                {results.chineseSign}
              </span>
            </div>
          </div>
        </div>

        {/* Детализированные результаты */}
        <div className="space-y-3 mb-6">
          <ResultItem 
            label="Дата рождения" 
            value={formatDisplayDate(results.birthdayDate)} 
            color="purple" 
            icon={<User className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Дата отсчета" 
            value={formatDisplayDate(results.referenceDate)} 
            color="cyan" 
            icon={<Calendar className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Полных лет" 
            value={`${results.years} ${results.years === 1 ? 'год' : (results.years >= 2 && results.years <= 4) ? 'года' : 'лет'}`} 
            color="violet" 
            icon={<CalendarClock className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Полных месяцев" 
            value={`${results.totalMonths} ${formatMonthsLabel(results.totalMonths)}`} 
            color="blue"
          />
          
          <ResultItem 
            label="Полных недель" 
            value={`${results.totalWeeks} ${formatWeeksLabel(results.totalWeeks)}`} 
            color="teal"
          />
          
          <ResultItem 
            label="Всего дней" 
            value={`${results.totalDays} ${formatDaysLabel(results.totalDays)}`} 
            color="green"
          />
          
          <ResultItem 
            label="Следующий день рождения" 
            value={formatDisplayDate(results.nextBirthday)} 
            color="orange" 
            icon={<Milestone className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Дней до следующего дня рождения" 
            value={`${results.daysToBirthday} ${formatDaysLabel(results.daysToBirthday)}`} 
            color="red"
          />
          
          {results.isLeapYearBorn && (
            <ResultItem 
              label="Родился(ась) в високосный год" 
              value="Да" 
              color="blue"
            />
          )}
        </div>
        
        {/* Дополнительная информация */}
        <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-4 mt-2 border border-violet-100 dark:border-violet-800">
          <h4 className="text-sm font-medium text-violet-900 dark:text-violet-100 mb-1 flex items-center gap-1.5">
            <User className="w-4 h-4 text-violet-500" />
            Интересные факты
          </h4>
          <p className="text-sm text-violet-800 dark:text-violet-200">
            {results.totalDays > 0 ? (
              <>
                За {results.totalDays} дней жизни сердце человека совершает около {formatNumber(results.totalDays * 24 * 60 * 70, 0)} ударов, 
                а Земля преодолела примерно {formatNumber(results.totalDays / 365.25 * 940, 0)} миллионов километров вокруг Солнца.
              </>
            ) : 'Возраст рассчитать не удалось.'}
          </p>
        </div>
      </ToolResult>
    );
  }
  
  // По умолчанию возвращаем пустой компонент
  return null;
};

export default ResultsDisplay;